<template>
  <div class="referral-commission-dashboard">
    <!-- Header with statistics -->
    <div class="dashboard-header">
      <div class="stats-cards">
        <div class="stat-card">
          <h3>Total Commissions (10%)</h3>
          <p class="stat-value">{{ formatCurrency(totalCommissionsAmount) }}</p>
        </div>
        <div class="stat-card">
          <h3>This Month</h3>
          <p class="stat-value">{{ formatCurrency(monthlyCommissionsAmount) }}</p>
        </div>
        <div class="stat-card">
          <h3>Active Referrals</h3>
          <p class="stat-value">{{ activeReferralsCount }}</p>
        </div>
        <div class="stat-card">
          <h3>Available Balance</h3>
          <p class="stat-value">{{ formatCurrency(availableBalance) }}</p>
        </div>
      </div><button class="quick-action-btn" @click="goTo('/savings/loans/active')">
  <div class="qa-icon">
    <div class="loan-icon">
      <div class="loan-card"></div>
      <div class="loan-dollar">$</div>
    </div>
  </div>
  <span>Get Loan</span>
</button>

      <!-- Monthly totals -->
      <div class="monthly-totals" style="margin-top: 24px;">
        <h4 style="margin-bottom: 16px; color: #374151; font-weight: 600;">
          Total Commissions per Month ({{ new Date().getFullYear() }})
        </h4>

        <div class="monthly-totals-grid">
          <div
            v-for="(total, index) in monthlyTotals"
            :key="index"
            class="monthly-total-card"
            :class="{ 'has-commission': total > 0 }"
          >
            <div class="month-name">{{ monthNames[index] }}</div>
            <div class="month-total">{{ formatCurrency(total) }}</div>
            <div v-if="total > 0" class="month-indicator">●</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="filter-row">
        <div class="filter-group">
          <label>Period:</label>
          <select v-model="selectedPeriod" @change="loadCommissions">
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Type:</label>
          <select v-model="selectedType" @change="loadCommissions">
            <option value="all">All Types</option>
            <option value="WITHDRAWAL_FEE">Withdrawal Fee</option>
            <option value="DEPOSIT_FEE">Deposit Fee</option>
            <option value="NJANGI_FEE">Njangi Commission</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Status:</label>
          <select v-model="selectedStatus" @change="loadCommissions">
            <option value="all">All Status</option>
            <option value="PAID">Paid</option>
            <option value="PENDING">Pending</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <button @click="refreshCommissions" :disabled="loadingState" class="refresh-btn">
          Refresh
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loadingState" class="loading-state">
      <p>Loading commissions...</p>
    </div>

    <!-- Error message -->
    <div v-if="errorMessage" class="error-state">
      <p>{{ errorMessage }}</p>
      <button @click="retryLoading" class="retry-btn">Retry</button>
    </div>

    <!-- Commissions table -->
    <div v-if="!loadingState && !errorMessage" class="commissions-table-container">
      <table class="commissions-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Referral</th>
            <th>Type</th>
            <th>Base Amount</th>
            <th>Fee</th>
            <th>Rate</th>
            <th>Commission</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="commission in paginatedCommissions" :key="commission.id">
            <td>{{ formatDate(commission.created_at) }}</td>
            <td>{{ commission.referred_user_username }}</td>
            <td>
              <span :class="['commission-type', getTypeClass(commission.commission_type)]">
                {{ getTypeLabel(commission.commission_type) }}
              </span>
            </td>
            <td>{{ formatCurrency(commission.base_amount) }}</td>
            <td>{{ formatCurrency(commission.fee_amount) }}</td>
            <td>{{ commission.commission_rate }}%</td>
            <td class="commission-amount">{{ formatCurrency(commission.commission_amount) }}</td>
            <td>
              <span :class="['status-badge', getStatusClass(commission.status)]">
                {{ getStatusLabel(commission.status) }}
              </span>
            </td>
            <td>
              <button @click="viewCommissionDetails(commission)" class="action-btn view-btn">
                Details
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination">
        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage <= 1"
          class="pagination-btn"
        >
          Previous
        </button>

        <span class="page-info">
          Page {{ currentPage }} of {{ totalPages }}
        </span>

        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage >= totalPages"
          class="pagination-btn"
        >
          Next
        </button>
      </div>

      <!-- No commissions message -->
      <div v-if="filteredCommissions.length === 0" class="no-commissions">
        <p>No commissions found for the selected filters.</p>
      </div>
    </div>

    <!-- Commission details modal -->
    <div v-if="selectedCommission" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Commission Details</h3>
          <button @click="closeModal" class="close-btn">&times;</button>
        </div>

        <div class="modal-body">
          <div class="detail-row">
            <span class="detail-label">Transaction ID:</span>
            <span class="detail-value">{{ selectedCommission.source_transaction_id }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Referral:</span>
            <span class="detail-value">{{ selectedCommission.referred_user_username }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Type:</span>
            <span class="detail-value">{{ getTypeLabel(selectedCommission.commission_type) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Date:</span>
            <span class="detail-value">{{ formatDateTime(selectedCommission.created_at) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Base Amount:</span>
            <span class="detail-value">{{ formatCurrency(selectedCommission.base_amount) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Fee:</span>
            <span class="detail-value">{{ formatCurrency(selectedCommission.fee_amount) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Commission Rate:</span>
            <span class="detail-value">{{ selectedCommission.commission_rate }}%</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Commission:</span>
            <span class="detail-value commission-highlight">{{ formatCurrency(selectedCommission.commission_amount) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Status:</span>
            <span :class="['detail-value', 'status-badge', getStatusClass(selectedCommission.status)]">
              {{ getStatusLabel(selectedCommission.status) }}
            </span>
          </div>
          <div v-if="selectedCommission.notes" class="detail-row">
            <span class="detail-label">Notes:</span>
            <span class="detail-value">{{ selectedCommission.notes }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ReferralCommissionDashboard',

  data() {
    return {
      rawCommissions: [],
      selectedPeriod: 'all',
      selectedType: 'all',
      selectedStatus: 'all',
      loadingState: false,
      errorMessage: null,
      retryAttempts: 0,
      selectedCommission: null,
      currentPage: 1,
      pageSize: 20,
      totalCommissions: 0,

      // Statistics
      totalCommissionsAmount: 0,
      monthlyCommissionsAmount: 0,
      activeReferralsCount: 0,
      availableBalance: 0,

      // Month names
      monthNames: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
    };
  },

  computed: {
    filteredCommissions() {
      return this.rawCommissions.filter(commission => {
        // Filter by period
        if (this.selectedPeriod !== 'all') {
          const commissionDate = new Date(commission.created_at);
          const now = new Date();

          switch (this.selectedPeriod) {
            case 'today':
              if (commissionDate.toDateString() !== now.toDateString()) return false;
              break;
            case 'week':
              const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              if (commissionDate < weekAgo) return false;
              break;
            case 'month':
              if (commissionDate.getMonth() !== now.getMonth() ||
                  commissionDate.getFullYear() !== now.getFullYear()) return false;
              break;
            case 'year':
              if (commissionDate.getFullYear() !== now.getFullYear()) return false;
              break;
          }
        }

        // Filter by type
        if (this.selectedType !== 'all' && commission.commission_type !== this.selectedType) {
          return false;
        }

        // Filter by status
        if (this.selectedStatus !== 'all' && commission.status !== this.selectedStatus) {
          return false;
        }

        return true;
      });
    },

    totalPages() {
      return Math.ceil(this.filteredCommissions.length / this.pageSize);
    },

    paginatedCommissions() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredCommissions.slice(start, end);
    },

    // Monthly totals for current year
    monthlyTotals() {
      const totals = new Array(12).fill(0);
      const currentYear = new Date().getFullYear();

      const paidCommissions = this.rawCommissions.filter(commission =>
        commission.status === 'PAID' &&
        new Date(commission.created_at).getFullYear() === currentYear
      );

      paidCommissions.forEach(commission => {
        const commissionDate = new Date(commission.created_at);
        const month = commissionDate.getMonth();
        totals[month] += parseFloat(commission.commission_amount) || 0;
      });

      return totals;
    }
  },

  async created() {
    document.title = "Referral Commissions Dashboard";
    await this.initializeComponent();
  },

  methods: {
    async initializeComponent() {
      try {
        await this.loadCommissions();
        await this.loadStatistics();
      } catch (error) {
        this.handleInitializationError(error);
      }
    },

    async loadCommissions() {
      if (this.retryAttempts > 2) return;

      this.loadingState = true;
      this.errorMessage = null;

      try {
        const response = await axios.get('/api/v1/home/referral/commissions/', {
          params: {
            period: this.selectedPeriod,
            type: this.selectedType,
            status: this.selectedStatus,
            page: this.currentPage,
            page_size: this.pageSize
          },
          timeout: 15000
        });

        if (!Array.isArray(response.data.results)) {
          throw new Error('Invalid data format');
        }

        this.rawCommissions = response.data.results;
        this.totalCommissions = response.data.count || 0;
        this.retryAttempts = 0;
      } catch (error) {
        this.handleCommissionError(error);
      } finally {
        this.loadingState = false;
      }
    },

    async loadStatistics() {
      try {
        const response = await axios.get('/api/v1/home/referral/commissions/stats/', {
          timeout: 10000
        });

        const stats = response.data;

        console.log('📊 API Response:', stats);

        // Use exact property names returned by Django API
        this.totalCommissionsAmount = parseFloat(stats.total_commissions) || 0;
        this.monthlyCommissionsAmount = parseFloat(stats.monthly_commissions) || 0;
        this.activeReferralsCount = parseInt(stats.active_referrals) || 0;
        this.availableBalance = parseFloat(stats.available_balance) || 0;

        console.log('✅ Values assigned:', {
          totalCommissionsAmount: this.totalCommissionsAmount,
          monthlyCommissionsAmount: this.monthlyCommissionsAmount,
          activeReferralsCount: this.activeReferralsCount,
          availableBalance: this.availableBalance
        });

      } catch (error) {
        console.error('❌ Error loading statistics:', error);
        console.error('Details:', error.response?.data || error.message);

        // Default values on error
        this.totalCommissionsAmount = 0;
        this.monthlyCommissionsAmount = 0;
        this.activeReferralsCount = 0;
        this.availableBalance = 0;

        // Show error to user
        this.$store.commit('setSnackBarMessage', 'Error loading statistics');
        this.$store.commit('setSnackBarColor', "red darken-4");
        this.$store.commit('activateSnackBar', true);
      }
    },

    async refreshCommissions() {
      this.retryAttempts = 0;
      this.currentPage = 1;
      await this.loadCommissions();
      await this.loadStatistics();
    },

    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.currentPage = newPage;
        this.loadCommissions();
      }
    },

    viewCommissionDetails(commission) {
      this.selectedCommission = commission;
    },

    closeModal() {
      this.selectedCommission = null;
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return '0 FCFA';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'XAF',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount).replace('XAF', 'FCFA');
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getTypeLabel(type) {
      const labels = {
        'WITHDRAWAL_FEE': 'Withdrawal Fee',
        'DEPOSIT_FEE': 'Deposit Fee',
        'NJANGI_FEE': 'Njangi Commission'
      };
      return labels[type] || type;
    },

    getTypeClass(type) {
      return type.toLowerCase().replace('_', '-');
    },

    getStatusLabel(status) {
      const labels = {
        'PAID': 'Paid',
        'PENDING': 'Pending',
        'CANCELLED': 'Cancelled'
      };
      return labels[status] || status;
    },

    getStatusClass(status) {
      return `status-${status.toLowerCase()}`;
    },

    handleCommissionError(error) {
      this.errorMessage = error.response?.data?.error ||
                        error.message ||
                        'Error loading commissions';
      this.retryAttempts++;

      if (this.retryAttempts <= 2) {
        setTimeout(() => this.loadCommissions(), 2000 * this.retryAttempts);
      }
    },

    handleInitializationError(error) {
      this.errorMessage = `Initialization error: ${error.message}`;
      console.error('Component initialization error:', error);
    },

    retryLoading() {
      this.retryAttempts = 0;
      this.initializeComponent();
    }
  }
};
</script>

<style scoped>
/* Referral Commission Dashboard - CSS Styles */

.referral-commission-dashboard {
  padding: 24px;
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Header with statistics */
.dashboard-header {
  margin-bottom: 32px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 16px;
}

.stat-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.25);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.35);
}

.stat-card h3 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 500;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
}

/* Monthly totals styles */
.monthly-totals {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.monthly-totals h4 {
  margin: 0 0 16px 0;
  color: #374151;
  font-weight: 600;
  font-size: 16px;
}

.monthly-totals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.monthly-total-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  position: relative;
  transition: all 0.2s ease;
}

.monthly-total-card.has-commission {
  background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%);
  border-color: #10b981;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
}

.monthly-total-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.month-name {
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.monthly-total-card.has-commission .month-name {
  color: #059669;
}

.month-total {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.monthly-total-card.has-commission .month-total {
  color: #059669;
}

.month-indicator {
  position: absolute;
  top: 6px;
  right: 8px;
  color: #10b981;
  font-size: 12px;
  opacity: 0.8;
}

/* Filters section */
.filters-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.filter-row {
  display: flex;
  gap: 20px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  min-width: 160px;
}

.filter-group label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.filter-group select {
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease;
}

.filter-group select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.refresh-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading and error states */
.loading-state, .error-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  margin: 24px 0;
}

.error-state {
  color: #ef4444;
}

.retry-btn {
  background: #ef4444;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  margin-top: 16px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.retry-btn:hover {
  background: #dc2626;
}

/* Commissions table */
.commissions-table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.commissions-table {
  width: 100%;
  border-collapse: collapse;
}

.commissions-table thead {
  background: #f9fafb;
}

.commissions-table th {
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e5e7eb;
}

.commissions-table td {
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
  color: #1f2937;
}

.commissions-table tbody tr:hover {
  background: #f9fafb;
}

.commission-amount {
  font-weight: 600;
  color: #059669;
}

.commission-type {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.commission-type.withdrawal-fee {
  background: #fef3c7;
  color: #92400e;
}

.commission-type.deposit-fee {
  background: #dbeafe;
  color: #1e40af;
}

.commission-type.njangi-fee {
  background: #e0e7ff;
  color: #3730a3;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.status-badge.status-paid {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.status-cancelled {
  background: #fee2e2;
  color: #991b1b;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-btn {
  background: #667eea;
  color: white;
}

.view-btn:hover {
  background: #5a67d8;
  transform: translateY(-1px);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #667eea;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

/* No commissions message */
.no-commissions {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  background: white;
  border-radius: 12px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.modal-header h3 {
  margin: 0;
  color: #1f2937;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
}


.close-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 500;
  color: #6b7280;
  flex: 0 0 40%;
}

.detail-value {
  color: #1f2937;
  font-weight: 500;
  text-align: right;
}

.commission-highlight {
  color: #059669 !important;
  font-size: 18px;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 768px) {
  .referral-commission-dashboard {
    padding: 16px;
  }
  
  .stats-cards {
    grid-template-columns: 1fr;
  }
  
  .monthly-totals-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
  
  .monthly-total-card {
    padding: 8px;
  }
  
  .month-name {
    font-size: 11px;
  }
  
  .month-total {
    font-size: 14px;
  }
  
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .commissions-table-container {
    overflow-x: auto;
  }
  
  .commissions-table {
    min-width: 800px;
  }
  
  .modal-content {
    margin: 20px;
    width: calc(100% - 40px);
  }
}

@media (max-width: 480px) {
  .monthly-totals-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Animations */
.referral-commission-dashboard * {
  transition: all 0.2s ease;
}

/* Scrollbar personnalisée */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.loan-icon {
  position: relative;
  width: 56px;
  height: 56px;
}
.loan-card {
  position: absolute;
  top: 10px;
  left: 5px;
  width: 36px;
  height: 24px;
  border: 2.5px solid #1a56db;
  border-radius: 4px;
  background: #eff6ff;
}
.loan-card::before {
  content: '';
  position: absolute;
  top: 5px;
  left: 0; right: 0;
  height: 4px;
  background: #1a56db;
  opacity: 0.35;
}
.loan-card::after {
  content: '';
  position: absolute;
  top: 14px;
  left: 4px;
  width: 10px;
  height: 3px;
  background: #1a56db;
  border-radius: 2px;
  opacity: 0.5;
}
.loan-dollar {
  position: absolute;
  bottom: 7px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #1a56db;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  border: 2px solid #eff6ff;
}
</style>